/**
 * 페이월 모달 (Issue #446 PR3)
 *
 * IonModal → F7 Popup (full screen). Page + Navbar 구조.
 * BillingAdapter는 PR4에서 호출처 정리 — 본 PR은 기존 subscription.service 호출 유지.
 */

import { useEffect, useState } from "react";
import {
  Popup,
  Page,
  Navbar,
  NavRight,
  Link,
  Block,
  Button,
  Preloader,
  Icon,
} from "framework7-react";
import { useTranslation } from "react-i18next";
import type { PurchasesPackageLike } from "@tldr/core";
import { useSubscription } from "../../hooks/useSubscription";
import * as subscriptionService from "../../services/subscription.service";
import "./PaywallModal.css";

interface PaywallModalProps {
  isOpen: boolean;
  onDismiss: () => void;
}

export function PaywallModal({ isOpen, onDismiss }: PaywallModalProps) {
  const { t } = useTranslation("subscription");
  const { refreshSubscription } = useSubscription();
  const [packages, setPackages] = useState<PurchasesPackageLike[]>([]);
  const [isLoadingOfferings, setIsLoadingOfferings] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    setIsLoadingOfferings(true);
    setError(null);

    subscriptionService.getOfferings().then((result) => {
      if (result.success && result.data.current) {
        setPackages(result.data.current.availablePackages);
      } else if (!result.success) {
        setError(result.error.message);
      }
      setIsLoadingOfferings(false);
    });
  }, [isOpen]);

  const handlePurchase = async (pkg: PurchasesPackageLike) => {
    setIsPurchasing(true);
    setError(null);

    const result = await subscriptionService.purchasePackage(pkg);

    if (result.success) {
      if (result.data) {
        await refreshSubscription();
        onDismiss();
      }
      // null = 사용자 취소, 모달 유지
    } else {
      setError(result.error.message);
    }

    setIsPurchasing(false);
  };

  const handleRestore = async () => {
    setIsPurchasing(true);
    setError(null);

    const result = await subscriptionService.restorePurchases();

    if (result.success) {
      if (result.data.isPro) {
        await refreshSubscription();
        onDismiss();
      } else {
        setError(t("paywall.noRestoreFound"));
      }
    } else {
      setError(result.error.message);
    }

    setIsPurchasing(false);
  };

  return (
    <Popup
      className="paywall-modal"
      opened={isOpen}
      onPopupClosed={onDismiss}
    >
      <Page>
        <Navbar title={t("paywall.title")}>
          <NavRight>
            <Link popupClose>
              <Icon f7="xmark" />
            </Link>
          </NavRight>
        </Navbar>

        <Block strong className="paywall-modal__benefits">
          <h2>{t("paywall.subtitle")}</h2>
          <ul>
            <li>{t("paywall.benefits.unlimitedSummaries")}</li>
            <li>{t("paywall.benefits.aiChat")}</li>
            <li>{t("paywall.benefits.priorityProcessing")}</li>
          </ul>
        </Block>

        {isLoadingOfferings && (
          <Block className="paywall-modal__loading">
            <Preloader />
          </Block>
        )}

        {!isLoadingOfferings && packages.length > 0 && (
          <Block className="paywall-modal__packages">
            {packages.map((pkg) => (
              <Button
                key={pkg.identifier}
                fill
                large
                onClick={() => handlePurchase(pkg)}
                disabled={isPurchasing}
              >
                {isPurchasing ? (
                  <Preloader size={20} />
                ) : (
                  `${pkg.product.title} - ${pkg.product.priceString}`
                )}
              </Button>
            ))}
          </Block>
        )}

        {error && (
          <Block>
            <p className="paywall-modal__error">{error}</p>
          </Block>
        )}

        <Block className="paywall-modal__restore">
          <Button small onClick={handleRestore} disabled={isPurchasing}>
            {t("paywall.restore")}
          </Button>
        </Block>
      </Page>
    </Popup>
  );
}
